import { perUnitDecimalPlaces } from '@calculations/utilities/selling-uom';
import { SellingUnitOfMeasure } from '@snap-types/models/enums';

export enum SnapRoundingMethod {
  None = 'None',
  Percent = 'Percent',
  DecimalPercent = 'DecimalPercent',
  Decimal = 'Decimal',
  Custom = 'Custom',
}

export const SnapRoundingOptionsNone = {
  method: SnapRoundingMethod.None,
} as const;
export const SnapRoundingOptionsPercent = {
  method: SnapRoundingMethod.Percent,
} as const;
export const SnapRoundingOptionsDecimalPercent = {
  method: SnapRoundingMethod.DecimalPercent,
} as const;
export const SnapRoundingOptionsDecimal = {
  method: SnapRoundingMethod.Decimal,
} as const;

/**
 * A site's configured price precision together with the unit that precision is
 * expressed in. Both are needed, because `decimalPlaces` describes the price as
 * the site quotes it while SNAP stores per-unit values in eaches.
 *
 * Site configs and the quote builder store both fields side by side, so callers
 * pass whichever object they already hold.
 */
export type SnapPricingBasis = {
  decimalPlaces: number;
  sellingUnitOfMeasure: SellingUnitOfMeasure;
};

/**
 * The basis of a site quoting in ones, where the per-unit and total precisions
 * coincide and rounding is unaffected by this module's unit handling.
 *
 * Named rather than defaulted so that a caller states the assumption instead of
 * falling into it — an omitted selling unit of measure is invisible on every
 * site but ADF, which is how this class of defect has gone unnoticed before.
 */
export function pricingBasisInEaches(decimalPlaces: number): SnapPricingBasis {
  return { decimalPlaces, sellingUnitOfMeasure: SellingUnitOfMeasure.EA };
}

/**
 * Custom rounding resolves to two precisions: `decimalPlaces` for currency
 * totals, which are basis-independent, and `perUnitDecimalPlaces` for values
 * held per each. Use `snapRound` for the former and `snapRoundPerUnit` for the
 * latter — on an `EA` site they are equal, so only a thousands site tells them
 * apart.
 */
export function SnapRoundingOptionsCustom(basis: SnapPricingBasis) {
  return {
    method: SnapRoundingMethod.Custom,
    decimalPlaces: basis.decimalPlaces,
    perUnitDecimalPlaces: perUnitDecimalPlaces(
      basis.decimalPlaces,
      basis.sellingUnitOfMeasure,
    ),
  };
}

export type SnapRoundingOptions =
  | typeof SnapRoundingOptionsNone
  | typeof SnapRoundingOptionsPercent
  | typeof SnapRoundingOptionsDecimalPercent
  | typeof SnapRoundingOptionsDecimal
  | ReturnType<typeof SnapRoundingOptionsCustom>;

export const SnapRoundingDecimalPlaces = {
  [SnapRoundingMethod.Percent]: 2,
  [SnapRoundingMethod.DecimalPercent]: 4,
  [SnapRoundingMethod.Decimal]: 8,
} as const;

// Check that DecimalPercent uses 2 decimal places more than Percent
/* v8 ignore next 9 lines */
if (
  SnapRoundingDecimalPlaces.DecimalPercent -
    SnapRoundingDecimalPlaces.Percent !==
  2
) {
  throw new Error(
    'SnapRoundingDecimalPlaces.DecimalPercent must be 2 greater than SnapRoundingDecimalPlaces.Percent',
  );
}
